import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Bed, Bath, Square, MapPin, Star, Calendar, Phone, Mail, Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import LeadCaptureModal from "@/components/lead-capture-modal";
import { useState } from "react";
import type { Property, Agent } from "@shared/schema";

export default function PropertyDetail() {
  const { id } = useParams();
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [leadType, setLeadType] = useState<"tour" | "contact">("tour");
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { data: property, isLoading: propertyLoading } = useQuery<any>({
    queryKey: ["/api/properties", id],
    enabled: !!id,
  });

  const { data: agent } = useQuery<Agent>({
    queryKey: ["/api/agents", property?.agentId],
    enabled: !!property?.agentId,
  });

  if (propertyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-ocean-blue border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-deep-navy mb-4">Property Not Found</h1>
          <Link href="/">
            <Button className="bg-ocean-blue hover:bg-bright-blue">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Properties
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleScheduleTour = () => {
    setLeadType("tour");
    setIsLeadModalOpen(true);
  };

  const handleContactAgent = () => {
    setLeadType("contact");
    setIsLeadModalOpen(true);
  };

  const openGallery = (index: number = 0) => {
    setCurrentImageIndex(index);
    setIsGalleryOpen(true);
  };

  const images = ((property as any)?.photos as string[])?.length
    ? ((property as any).photos as string[])
    : (property?.images || []);

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === (images.length || 1) - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? (images.length || 1) - 1 : prev - 1
    );
  };

  const propertyImages = images;
  const hasMultipleImages = propertyImages.length > 1;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link href="/">
            <Button variant="ghost" className="text-deep-navy hover:text-ocean-blue">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Properties
            </Button>
          </Link>
        </div>
      </div>

      {/* Property Gallery */}
      <div className="relative">
        {/* Main Image */}
        <div className="relative h-96 md:h-[500px] cursor-pointer" onClick={() => openGallery(0)}>
          <div
            className="absolute inset-0 bg-cover bg-center transition-all duration-300 hover:scale-105"
            style={{
              backgroundImage: `url('${propertyImages[0] || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&h=600&fit=crop"}')`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          
          {/* Image Counter */}
          {hasMultipleImages && (
            <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
              1 of {propertyImages.length}
            </div>
          )}
          
          {/* View All Photos Button */}
          {hasMultipleImages && (
            <div className="absolute top-4 left-4">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  openGallery(0);
                }}
                variant="outline"
                className="bg-black/70 border-white/30 text-white hover:bg-white hover:text-black"
                data-testid="button-view-all-photos"
              >
                View All {propertyImages.length} Photos
              </Button>
            </div>
          )}

          <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
            <div className="text-white">
              <div className="text-4xl font-playfair font-bold text-luxury-gold mb-2">
                ${typeof property.price === 'number' ? property.price.toLocaleString() : parseInt(property.price || "0").toLocaleString()}
              </div>
              <div className="flex items-center text-lg">
                <MapPin className="w-5 h-5 mr-2" />
                {property.address}, {property.city}, {property.state} {property.zipCode}
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleScheduleTour();
                }}
                className="bg-ocean-blue hover:bg-bright-blue text-white px-6 py-3"
                data-testid="button-schedule-tour"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Tour
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleContactAgent();
                }}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-deep-navy px-6 py-3"
                data-testid="button-contact-agent"
              >
                <Phone className="w-4 h-4 mr-2" />
                Contact Agent
              </Button>
            </div>
          </div>
        </div>

        {/* Thumbnail Gallery */}
        {hasMultipleImages && (
          <div className="flex gap-2 p-4 overflow-x-auto">
            {propertyImages.slice(0, 6).map((image, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 cursor-pointer rounded-lg overflow-hidden border-2 border-transparent hover:border-ocean-blue transition-all"
                onClick={() => openGallery(index)}
                data-testid={`thumbnail-${index}`}
              >
                <img
                  src={image}
                  alt={`Property view ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {propertyImages.length > 6 && (
              <div
                className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-all"
                onClick={() => openGallery(6)}
                data-testid="button-more-photos"
              >
                <span className="text-xs text-gray-600 font-semibold">+{propertyImages.length - 6}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Property Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="mb-8">
              <h1 className="font-playfair text-4xl font-bold text-deep-navy mb-4">
                {property.title}
              </h1>
              
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center p-6 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-center mb-2">
                    <Bed className="w-6 h-6 text-ocean-blue mr-2" />
                    <span className="text-3xl font-bold text-ocean-blue">{property.bedrooms}</span>
                  </div>
                  <div className="text-sm text-gray-600">Bedrooms</div>
                </div>
                <div className="text-center p-6 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-center mb-2">
                    <Bath className="w-6 h-6 text-ocean-blue mr-2" />
                    <span className="text-3xl font-bold text-ocean-blue">{property.bathrooms}</span>
                  </div>
                  <div className="text-sm text-gray-600">Bathrooms</div>
                </div>
                <div className="text-center p-6 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-center mb-2">
                    <Square className="w-6 h-6 text-ocean-blue mr-2" />
                    <span className="text-3xl font-bold text-ocean-blue">{(property.squareFeet || (property as any).sqft || 0).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-gray-600">Sq Ft</div>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="font-playfair text-2xl font-semibold text-deep-navy mb-4">Description</h3>
                <p className="text-gray-600 leading-relaxed">
                  {property.description}
                </p>
              </div>

              {property.features && property.features.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-playfair text-2xl font-semibold text-deep-navy mb-4">Property Features</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {property.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-gray-600">
                        <Check className="w-5 h-5 text-mint-green mr-3 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Agent Card */}
            {agent && (
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <img
                      src={agent.photo || "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&fit=crop&crop=face"}
                      alt={agent.name}
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h4 className="font-semibold text-deep-navy text-lg">{agent.name}</h4>
                      <p className="text-sm text-gray-600">{agent.specialties?.[0] || "Real Estate Agent"}</p>
                      <div className="flex items-center text-xs text-luxury-gold mt-1">
                        <Star className="w-4 h-4 fill-current mr-1" />
                        <span>{agent.rating} ({agent.reviewCount} reviews)</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Button
                      onClick={handleScheduleTour}
                      className="w-full bg-ocean-blue hover:bg-bright-blue text-white"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule Private Showing
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-ocean-blue text-ocean-blue hover:bg-ocean-blue hover:text-white"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call {agent.phone}
                    </Button>
                    <Button
                      onClick={handleContactAgent}
                      className="w-full bg-luxury-gold text-deep-navy hover:bg-yellow-500"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mortgage Calculator */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-playfair text-xl font-semibold text-deep-navy mb-4">
                  Mortgage Calculator
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="downPayment" className="text-sm font-medium text-deep-navy">
                      Down Payment
                    </Label>
                    <Input
                      id="downPayment"
                      defaultValue={`$${(typeof property.price === 'number' ? property.price * 0.2 : parseInt(property.price || "0") * 0.2).toLocaleString()}`}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="interestRate" className="text-sm font-medium text-deep-navy">
                      Interest Rate
                    </Label>
                    <Input
                      id="interestRate"
                      defaultValue="6.5%"
                      className="mt-1"
                    />
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">Estimated Monthly Payment</div>
                    <div className="text-2xl font-bold text-ocean-blue">
                      ${Math.round(((typeof property.price === 'number' ? property.price : parseInt(property.price || "0")) * 0.8 * 0.065) / 12).toLocaleString()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <LeadCaptureModal
        isOpen={isLeadModalOpen}
        onClose={() => setIsLeadModalOpen(false)}
        propertyId={property.id}
        propertyTitle={property.title}
        leadType={leadType}
      />

      {/* Full-Screen Photo Gallery Modal */}
      {isGalleryOpen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <Button
              onClick={() => setIsGalleryOpen(false)}
              variant="ghost"
              className="absolute top-4 right-4 text-white hover:bg-white/20 z-10"
              data-testid="button-close-gallery"
            >
              <X className="w-6 h-6" />
            </Button>

            {/* Image Counter */}
            <div className="absolute top-4 left-4 text-white bg-black/50 px-4 py-2 rounded-full z-10">
              {currentImageIndex + 1} of {propertyImages.length}
            </div>

            {/* Navigation Arrows */}
            {hasMultipleImages && (
              <>
                <Button
                  onClick={prevImage}
                  variant="ghost"
                  className="absolute left-4 text-white hover:bg-white/20 z-10"
                  data-testid="button-prev-image"
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>
                <Button
                  onClick={nextImage}
                  variant="ghost"
                  className="absolute right-4 text-white hover:bg-white/20 z-10"
                  data-testid="button-next-image"
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>
              </>
            )}

            {/* Main Image */}
            <img
              src={propertyImages[currentImageIndex] || propertyImages[0]}
              alt={`Property view ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              data-testid={`gallery-image-${currentImageIndex}`}
            />
          </div>

          {/* Thumbnail Strip */}
          {hasMultipleImages && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 max-w-4xl overflow-x-auto p-2">
              {propertyImages.map((image, index) => (
                <div
                  key={index}
                  className={`flex-shrink-0 w-16 h-16 cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentImageIndex ? 'border-luxury-gold' : 'border-transparent hover:border-white/50'
                  }`}
                  onClick={() => setCurrentImageIndex(index)}
                  data-testid={`gallery-thumbnail-${index}`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
